import { Colors, Heading, Icon, Text } from '@amelio/ui-library';

import { useAmelioTranslations } from '@/hooks';
import { MEETINGS_TITLE } from '@/pages/ThematicMeetings/components/meetingStyles';

interface CareerAspirationBannerProps {
    employeeFirstName: string;
}

/**
 * The band the career aspirations open on: what the section is for, whose meeting it is, and who
 * reads the answers. The claim covers the whole card because every field of it now shares one
 * reach: the aspiration routes admit a management permission and then exclude the caller from
 * their own scope, and the employee's portal reads the target position from the career step their
 * manager planned, never from these answers.
 *
 * "Your management line" is the wording the module uses everywhere for this audience, and it is a
 * deliberate shortcut: a company-wide performance permission reaches every active employee without
 * standing in the line, and reach follows the org chart of the moment.
 */
export const CareerAspirationBanner = ({ employeeFirstName }: CareerAspirationBannerProps) => {
    const { t } = useAmelioTranslations();

    return (
        <header
            className={
                'from-primary via-secondary-600 to-purple flex flex-wrap items-center justify-between gap-4 bg-gradient-to-r px-10 py-7'
            }
            data-testid={'thematic-meeting-career-aspiration-banner'}
        >
            <div className={'flex min-w-0 items-center gap-4'}>
                <span
                    className={
                        'flex size-12 shrink-0 items-center justify-center rounded-xl border border-white/30 bg-white/20'
                    }
                >
                    <Icon
                        name={'location-compass'}
                        size={'lg'}
                        color={Colors.WHITE}
                        ariaHidden
                    />
                </span>

                <div className={'flex min-w-0 flex-col gap-1'}>
                    <Heading
                        level={3}
                        color={Colors.WHITE}
                        className={MEETINGS_TITLE}
                    >
                        {t('performanceProfile.growth.aspiration.form.title')}
                    </Heading>

                    <Text
                        size={'small'}
                        color={Colors.WHITE}
                    >
                        {t('performanceProfile.growth.aspiration.form.subtitle', {
                            name: employeeFirstName,
                        })}
                    </Text>
                </div>
            </div>

            {/* The icon is decorative: the sentence beside it is the notice, and a screen reader
                reads it without a control to reach for. It keeps to the right even once the band
                wraps, because white reads at 5.03:1 over the violet end of the gradient and only
                1.97:1 over the turquoise one. */}
            <div className={'ml-auto flex items-center gap-2 self-start'}>
                <Icon
                    name={'security-lock'}
                    size={12}
                    color={Colors.WHITE}
                    ariaHidden
                />

                <Text
                    size={'small'}
                    color={Colors.WHITE}
                >
                    {t('performanceProfile.growth.aspiration.form.visibility')}
                </Text>
            </div>
        </header>
    );
};
